import React, { useEffect, useState } from 'react';
import { RepairService, Model } from '../types/BookingTypes';
import { useCart } from '../context/CartContext';
import ServiceDetailsModal from './ServiceDetailsModal';
import ReviewList from './ReviewList';
import { ModelReview, getModelReviews } from '../services/orderService';
import { isLoggedIn } from '../services/authService';
import { useNavigate, useLocation } from 'react-router-dom';

interface ServiceSelectorProps {
  services: RepairService[];
  selectedServices: RepairService[];
  onSelect: (services: RepairService[]) => void;
  onBack?: () => void;
  modelName?: string;
  model: Model;
}

const ServiceSelector: React.FC<ServiceSelectorProps> = ({ 
  services, 
  selectedServices, 
  onSelect,
  onBack,
  modelName,
  model
}) => {
  const { addToCart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<RepairService | null>(null);
  const [reviews, setReviews] = useState<ModelReview[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);


  useEffect(() => {
    const fetchReviews = async () => {
      if (model.id) {
        setIsLoadingReviews(true);
        try {
          const modelReviews = await getModelReviews(model.id);
          setReviews(modelReviews);
        } catch (error) {
          console.error('Error fetching reviews:', error);
        } finally {
          setIsLoadingReviews(false);
        }
      }
    };

    fetchReviews();
  }, [model.id]);

  const handleAuthenticationCheck = () => {
    if (!isLoggedIn()) {
      // Store current location to redirect back after login
      navigate('/login', { 
        state: { 
          from: location.pathname,
          model: model,
          selectedServices: selectedServices
        } 
      });
      return false;
    }
    return true;
  };

  const handleToggle = (service: RepairService) => {
    // Check authentication first
    if (!handleAuthenticationCheck()) {
      return;
    }

    const isSelected = selectedServices.some(s => s.id === service.id);
    const newServices = isSelected 
      ? selectedServices.filter(s => s.id !== service.id)
      : [...selectedServices, service];
    
    // Update local state immediately for responsive UI
    onSelect(newServices);
    
    // Fire and forget - instant operations
    if (isSelected) {
      // Remove from cart using optimistic removal
      removeFromCart(model.id, service.id);
    } else {
      // Add to cart - only add the new service
      addToCart(model, [service]);
    }
  };

  const handleServiceClick = (service: RepairService) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Services Section */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
              >
                <i className="fas fa-arrow-left text-gray-600"></i>
              </button>
            )}
            <h3 className="text-base font-medium text-gray-800">
              Select Services for {modelName}
            </h3>
          </div>

          {/* Services List */}
          <div className="p-4">
            <div className="space-y-3">
              {services.map((service) => {
                const isSelected = selectedServices.some(s => s.id === service.id);
                return (
                  <div
                    key={service.id}
                    className="relative w-full flex flex-col bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
                  >
                    {/* Main Content */}
                    <div className="p-4 flex items-start">
                      <div className="flex-shrink-0 w-16 h-16 bg-gray-50 rounded-xl p-2 mr-4 flex items-center justify-center">
                        <img
                          src={service.serviceImage}
                          alt={service.serviceName}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-medium text-gray-900 text-base mb-0.5 line-clamp-1">{service.serviceName}</h4>
                            <div className="text-xs text-gray-500">{service.serviceDuration} mins to repair</div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="text-base font-semibold text-blue-600">₹{Math.round(service.finalPrice || (service.actualPrice + (service.serviceCost || 0) + ((service.serviceCost || 0) * 0.18)))}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Warranty Info */}
                    <div className="px-4 py-2 bg-green-50 border-t border-green-100">
                      <div className="flex items-center gap-2 text-green-700">
                        <i className="fas fa-shield-alt text-sm"></i>
                        <span className="text-sm font-medium">
                          {service.warrantyPeriod || 90} Days Warranty till {new Date(Date.now() + ((service.warrantyPeriod || 90) * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
                      <button
                        onClick={() => handleServiceClick(service)}
                        className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                      >
                        <i className="fas fa-info-circle"></i>
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (service) {
                            handleToggle(service);
                            if (!isSelected && isLoggedIn()) {
                              // @ts-ignore
                              window.gtag_report_conversion();
                            }
                          }
                        }}
                        className={`ml-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 flex items-center justify-center min-w-[140px] ${
                          isSelected 
                            ? 'bg-minto-50 text-minto-500 hover:bg-minto-100 active:bg-minto-200' 
                            : 'bg-minto-500 text-white hover:bg-minto-600 active:bg-minto-700'
                        }`}
                      >
                        {isSelected ? (
                          <>
                            <i className="fas fa-trash-alt mr-1.5"></i>
                            Remove
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plus mr-1.5"></i>
                            Add to Cart
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        {reviews && reviews.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-3 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <i className="fas fa-star text-yellow-400 text-sm"></i>
              <h3 className="text-sm font-medium text-gray-800">Customer Reviews</h3>
            </div>
            <div className="p-3 mb-20">
              <ReviewList reviews={reviews} isLoading={isLoadingReviews} />
            </div>
          </div>
        )}
      </div>

      {selectedService && (
        <ServiceDetailsModal
          isOpen={!!selectedService}
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </>
  );
};

export default ServiceSelector; 